import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099AInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-A instructions for the Tax year 2025"
          description="Get clear instructions for Form 1099-A for 2025. Learn the filing requirements, follow step-by-step guidance to report the acquisition or abandonment"
          keywords=""
          canonical="resources/information-returns/form-1099-a-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-A</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Understand ACA Reporting Requirements & File the Right Form with
          Confidence
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=f1099ainstructions`}
          >
            E-file Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                  minHeight: '100%',
                }}
              >
                {/* <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                 
                </Stack> */}
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-a-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-A at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-A{' '}
                        </strong>{' '}
                        is used by lenders to report the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          acquisition or abandonment of secured property{' '}
                        </strong>{' '}
                        and related debt/property transfer information to the
                        IRS
                      </span>
                    </li>

                    <li>
                      <span>
                        Borrower copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}> January 31</strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>{' '}
                        if filing on paper or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          March 31
                        </strong>{' '}
                        if filing electronically.
                      </span>
                    </li>

                    <li>
                      <span>
                        This article covers{' '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions
                        </strong>
                        , including lender and borrower details, acquisition or
                        abandonment date in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 1</strong>,
                        outstanding principal balance in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>,
                        property description in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>,
                        personal liability information in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 4</strong>, and
                        fair market value of property in
                        <strong style={{ fontWeight: 600 }}> Box 5</strong>.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}> Penalties:</strong>{' '}
                        Late or incorrect filing penalties range from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per form
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  IRS Form 1099-A is used by lenders to report when they{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    acquire a secured property
                  </span>{' '}
                  or when a{' '}
                  <span style={{ fontWeight: 600 }}>
                    borrower abandons secured property.
                  </span>{' '}
                  This form helps the IRS track debts and property transfers
                  connected to your trade or business.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '4px solid #0077b6',
                    px: 1.2,
                    py: 1.5,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    {' '}
                    Important Updates for 2025?
                  </Typography>
                  <Typography variant="body1">
                    Mortgage and student loan forgiveness relief{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      expires on December 31, 2025.
                    </span>{' '}
                    The exclusion from income for discharged{' '}
                    <span style={{ fontWeight: 600 }}>
                      qualified principal residence mortgage debt
                    </span>{' '}
                    and{' '}
                    <span style={{ fontWeight: 600 }}>
                      qualified student loan debt
                    </span>{' '}
                    under{' '}
                    <span style={{ fontWeight: 600 }}>
                      IRC Section 108(f)(5)
                    </span>{' '}
                    will no longer apply unless extended.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 1099-A?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pose" sx={{ color: '#0202ff' }}>
                          Purpose of 1099-A Form
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#coor" sx={{ color: '#0202ff' }}>
                          Coordination with Form 1099-C
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whois" sx={{ color: '#0202ff' }}>
                          Who is required to file?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#file" sx={{ color: '#0202ff' }}>
                          1099-A Filing Timeline
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to fill out 1099-a form?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#import" sx={{ color: '#0202ff' }}>
                          Important deadlines for Form 1099-A?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pen" sx={{ color: '#0202ff' }}>
                          Penalties for Form 1099-A
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#step" sx={{ color: '#0202ff' }}>
                          Steps to e-file 1099-A Form with TaxZerone?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whatis"
                >
                  What is Form 1099-A?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 1099-A is an IRS information return used by lenders to
                  report when they acquire a secured property or when a borrower
                  abandons secured property. This form helps the IRS track debts
                  and property transfers connected to your trade or business.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="pose"
                >
                  Purpose of 1099-A Form
                </Typography>
                <Grid
                  container
                  spacing={2}
                  sx={{ mb: 3, justifyContent: 'center' }}
                >
                  <Grid size={{ md: 4, sm: 6, xs: 12 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        minHeight: '100%',
                        py: 3,
                        p: 2,
                        borderRadius: 4,
                        textAlign: 'center',
                        alignContent: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f0f2f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: {
                            md: 20,
                            sm: 18,
                            xs: 16,
                          },
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Acquisition of Property
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        When a lender takes full or partial ownership of
                        collateral to satisfy a debt
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ md: 4, sm: 6, xs: 12 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        minHeight: '100%',
                        py: 3,
                        p: 2,
                        borderRadius: 4,
                        textAlign: 'center',
                        alignContent: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f0f2f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: {
                            md: 20,
                            sm: 18,
                            xs: 16,
                          },
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Abandonment of Property
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        When a borrower abandons property used as security for a
                        loan
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ md: 4, sm: 6, xs: 12 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        minHeight: '100%',
                        py: 3,
                        p: 2,
                        borderRadius: 4,
                        textAlign: 'center',
                        alignContent: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f0f2f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: {
                            md: 20,
                            sm: 18,
                            xs: 16,
                          },
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Who Uses It?
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Any business or individual lending money in connection
                        with a trade or business—not just banks or financial
                        institutions
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="coor"
                >
                  Coordination with Form 1099-C
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form 1099-A reports on{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    acquisition or abandonment of secured property,
                  </span>{' '}
                  while{' '}
                  <Link
                    href="/resources/information-returns/form-1099-c-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1099-C
                  </Link>{' '}
                  reports on{' '}
                  <span style={{ fontWeight: 600 }}>cancellation of debt</span>.
                  Sometimes, both events can happen for the same borrower in the
                  same year—for example, a foreclosure that cancels a debt of
                  $600 or more.
                </Typography>
                <Typography variant="body1">The IRS rules are:</Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer
                    sx={{ border: '1px solid #828282', borderRadius: 2 }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              fontSize: 15,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Scenario
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              fontSize: 15,
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Action
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              fontWeight: 600,
                              width: 300,
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                            }}
                          >
                            Notes
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                              fontWeight: 600,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Debt canceled $600 or more with
                            foreclosure/abandoned property
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            File only{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              Form 1099-C{' '}
                            </span>{' '}
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'center',
                              backgroundColor: '#fff',
                            }}
                          >
                            Automatically satisfy the Form 1099-A reporting
                            requirement for the same debtor.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: 15,
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Reporting on Form 1099-C if skipping Form 1099-A
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Complete{' '}
                            <span style={{ fontWeight: 600 }}>
                              boxes 4, 5, and 7
                            </span>
                          </TableCell>

                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                              backgroundColor: '#fff',
                            }}
                          >
                            <span style={{ fontWeight: 600 }}> Box 4:</span>{' '}
                            Debt description or account number <br />
                            <span style={{ fontWeight: 600 }}>Box 5:</span> Date
                            of debt cancellation <br />
                            <span style={{ fontWeight: 600 }}>Box 7:</span>{' '}
                            Amount of debt canceled
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontWeight: 600,
                              fontSize: 15,
                            }}
                          >
                            Filing both Form 1099-A and Form 1099-C for the same
                            debtor
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              backgroundColor: '#fff',
                              textAlign: 'center',
                              fontSize: 15,
                            }}
                          >
                            Do <span style={{ fontWeight: 600 }}>not</span> fill
                            in boxes 4, 5, and 7 on Form 1099-C
                          </TableCell>

                          <TableCell
                            sx={{
                              textAlign: 'center',
                              backgroundColor: '#fff',
                              fontSize: 15,
                            }}
                          >
                            Information is already reported on 1099-A form,
                            avoid duplicate reporting.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  What Property is Covered?
                </Typography>
                <Typography variant="body1">Property includes</Typography>
                <Box component="ul" sx={{ mb: 1.2, '& li': { mb: 1.2 } }}>
                  <li>Real property or real estate property</li>
                  <li>Intangible property </li>
                  <li>Tangible property (business, trade or investment). </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '4px solid #0077b6',
                    px: 1.2,
                    py: 1.5,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    Exceptions – No reporting required:
                  </Typography>
                  <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                    <li>
                      Personal-use tangible property (e.g., a car used
                      individually){' '}
                    </li>
                    <li>
                      Property outside the U.S. if the borrower certifies they
                      are an exempt foreign person
                    </li>
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ backgroundColor: '#fff3f3', p: 2 }}
                  >
                    <span style={{ fontWeight: 600 }}> Important:</span> If the
                    lender knows the statement is false, reporting is still
                    required.
                  </Typography>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="whois"
                >
                  Who is required to file?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Lenders and entities are required to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-a/"
                    sx={{ color: '#0202ff' }}
                  >
                    efile Form 1099-A
                  </Link>{' '}
                  when they acquire an interest in property or are involved in
                  the foreclosure or abandonment of a loan. The rules include:
                </Typography>
                <Grid
                  container
                  spacing={2}
                  sx={{ mb: 3, justifyContent: 'center' }}
                >
                  <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        minHeight: '100%',
                        py: 3,
                        p: 2,
                        borderRadius: 4,
                        textAlign: 'center',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f0f2f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: {
                            md: 20,
                            sm: 18,
                            xs: 16,
                          },
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Multiple Owners of a Single Loan:
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        If a loan has multiple owners with undivided interests
                        (for example, in loan pools, fixed investment trusts, or
                        similar arrangements), the trustee, record owner, or
                        similar representative must file{' '}
                        <span style={{ fontWeight: 600 }}>
                          one Form 1099-A Form per borrower
                        </span>{' '}
                        on behalf of all owners.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        minHeight: '100%',
                        py: 3,
                        p: 2,
                        borderRadius: 4,
                        textAlign: 'center',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f0f2f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: {
                            md: 20,
                            sm: 18,
                            xs: 16,
                          },
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Governmental Units:
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Any governmental unit, or its subsidiary agencies, that
                        lends money secured by property must{' '}
                        <Link
                          href="/information-returns/efile-irs-form-1099-a/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          file 1099 a
                        </Link>
                        .
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        minHeight: '100%',
                        py: 3,
                        p: 2,
                        borderRadius: 4,
                        textAlign: 'center',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f0f2f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: {
                            md: 20,
                            sm: 18,
                            xs: 16,
                          },
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Subsequent Holders of a Loan:
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        If a loan is transferred to a new holder, the subsequent
                        holder is treated as the lender and must report events
                        occurring{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          after the transfer
                        </span>
                        .
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid size={{ md: 6, sm: 6, xs: 12 }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        minHeight: '100%',
                        py: 3,
                        p: 2,
                        borderRadius: 4,
                        textAlign: 'center',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f0f2f9',
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontSize: {
                            md: 20,
                            sm: 18,
                            xs: 16,
                          },
                          fontWeight: 600,
                          mb: 2,
                        }}
                      >
                        Multiple Lenders with Foreclosure or Property
                        Acquisition:
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        If multiple lenders have interests in a property, and
                        one lender forecloses or acquires the property in a way
                        that affects the other lenders’ security interests, the
                        other lenders must also file Form 1099-A for their
                        loans.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Example:</b> If a first trust holder forecloses a building,
                  the second trust holder must still{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-a/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 1099-A
                  </Link>{' '}
                  for their portion, even if the foreclosure proceeds didn’t
                  satisfy their loan.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="file"
                >
                  1099-A Filing Timeline
                </Typography>
                <Typography variant="body1">
                  <Link
                    href="/information-returns/efile-irs-form-1099-a/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 1099 a
                  </Link>{' '}
                  in the{' '}
                  <span style={{ fontWeight: 600 }}>
                    year following the calendar year
                  </span>{' '}
                  when you:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Acquire an interest in the property, or</li>
                  <li>
                    First know or have reason to know the property was
                    abandoned.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Understanding Abandonment
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Abandonment occurs when a borrower permanently discards
                  property. You have “reason to know” based on facts discovered
                  through reasonable inquiry.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Timing Rules:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    If a foreclosure, execution or similar sale is expected
                    within 3 months, report as of the acquisition date or
                    sale.{' '}
                  </li>
                  <li>
                    If action takes longer than 3 months, report at the end of
                    the 3-month period.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> For further details, please refer to the{' '}
                  <Link
                    href="/resources/information-returns/form-1099-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1099 due dates
                  </Link>{' '}
                  to ensure timely filing and avoid penalties.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="howto"
                >
                  How to fill out 1099-a form?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here are the step-by-step instructions to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-a/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    fill out Form 1099-A
                  </Link>
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 1: Enter the Lender’s information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1099a-lender's-details-img.webp" />
                  <source srcSet="/assets/images/f1099a-lender's-details-img.png" />
                  <img
                    src="/assets/images/f1099a-lender's-details-img.png"
                    loading="lazy"
                    alt="Form 1099-A-lender's details"
                    width={524}
                    height={206}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Lender’s name, address, along with the ZIP Code and
                  Taxpayer Identification Number (TIN)
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 2: Enter the Borrower’s information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1099a-borrower's-details-img.webp" />
                  <source srcSet="/assets/images/f1099a-borrower's-details-img.png" />
                  <img
                    src="/assets/images/f1099a-borrower's-details-img.png"
                    loading="lazy"
                    alt="Form 1099-A- Borrower's details"
                    width={524}
                    height={232}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Borrower’s name, address along with the Zip Code and
                  the Taxpayer Identification Number (TIN)
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Step 3: Acquisition or Abandonment Information
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Account Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1099a-account-img.webp" />
                  <source srcSet="/assets/images/f1099a-account-img.png" />
                  <img
                    src="/assets/images/f1099a-account-img.png"
                    loading="lazy"
                    alt=" Form 1099-A-account number"
                    width={525}
                    height={53}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The <span style={{ fontWeight: 600 }}> account number</span>{' '}
                  is used to identify a specific loan or account for a borrower.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If you are filing{' '}
                    <span style={{ fontWeight: 600 }}>
                      more than one Form 1099-A for the same borrower,
                    </span>{' '}
                    you must include an account number for each form.{' '}
                  </li>
                  <li>
                    Even if you’re filing only one form, the IRS{' '}
                    <span style={{ fontWeight: 600 }}>
                      encourages including an account number
                    </span>{' '}
                    to help distinguish between different loans or
                    accounts.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Box 1: Date of Lender's Acquisition or Knowledge of
                  Abandonment
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1099a-abandonment-img.webp" />
                  <source srcSet="/assets/images/f1099a-abandonment-img.png" />
                  <img
                    src="/assets/images/f1099a-abandonment-img.png"
                    loading="lazy"
                    alt="Form 1099-A-box-1-date of lender’s acquisition or knowledge of abandonment"
                    width={217}
                    height={79}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    date the property was acquired or abandoned:{' '}
                  </span>
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    For{' '}
                    <span style={{ fontWeight: 600 }}>acquisitions:</span>{' '}
                  </li>
                  <ul>
                    <li>
                      Enter the date you acquired the property, which is usually
                      the earlier of the date the title transfers to you or the
                      date of possession and ownership rights transfer.{' '}
                    </li>
                    <li>
                      If a legal objection period applies, record the date when
                      that period ends.{' '}
                    </li>
                    <li>
                      If you buy the property through a foreclosure or similar
                      sale, use the later of the sale date or the borrower’s
                      right of redemption expiration date.{' '}
                    </li>
                  </ul>
                  <li>
                    For{' '}
                    <span style={{ fontWeight: 600 }}>abandonments:</span>{' '}
                  </li>
                  <ul>
                    <li>
                      {' '}
                      Enter the date when you first knew, or should have known,
                      that the property was abandoned.{' '}
                    </li>
                    <li>
                      If you intend to begin a foreclosure or similar process
                      within 3 months, follow the timing rules discussed
                      earlier.{' '}
                    </li>
                    <li>
                      If a third party purchases the property at the sale, use
                      the date of sale as the date of abandonment.{' '}
                    </li>{' '}
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Box 2: Balance of Principal Outstanding
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1099a-balance-img.webp" />
                  <source srcSet="/assets/images/f1099a-balance-img.png" />
                  <img
                    src="/assets/images/f1099a-balance-img.png"
                    loading="lazy"
                    alt="Form 1099-A-box-2-balance of principal outstanding"
                    width={217}
                    height={79}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}> unpaid principal</span> of
                  the debt at the time you
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    acquired the property or first knew it was abandoned.
                  </span>
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    // mt: 0,
                    // ml: { sm: 5, xs: 1 },
                    // mr: { sm: 8, xs: 1 },
                    mb: 4,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> Do not include accrued interest or foreclosure
                  costs.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Box 4: Fair Market Value (FMV) of Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1099a-fair-market-img.webp" />
                  <source srcSet="/assets/images/f1099a-fair-market-img.png" />
                  <img
                    src="/assets/images/f1099a-fair-market-img.png"
                    loading="lazy"
                    alt="Form 1099-A-box-4-fair market value of property"
                    width={217}
                    height={78}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the
                  <span style={{ fontWeight: 600 }}> fair market value</span> of
                  the property:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      For a foreclosure, execution, or similar sale:
                    </span>{' '}
                    Use the sale proceeds as the FMV unless there is clear
                    evidence showing otherwise.{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      For abandonment or voluntary conveyance to the lender:
                    </span>{' '}
                    If you marked the box in Box 5, enter the appraised value of
                    the property.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Otherwise:</span> Leave
                    this box blank.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Box 5: Was Borrower Personally Liable for Repayment of the
                  Debt
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1099a-repayment-img.webp" />
                  <source srcSet="/assets/images/f1099a-repayment-img.png" />
                  <img
                    src="/assets/images/f1099a-repayment-img.png"
                    loading="lazy"
                    alt="check if the borrower was personally liable for repayment of the debt"
                    width={432}
                    height={54}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the checkbox, if the borrower was{' '}
                  <span style={{ fontWeight: 600 }}>personally liable</span> for
                  repaying the debt when it was{' '}
                  <span style={{ fontWeight: 600 }}>originally created</span>{' '}
                  or, if modified, at the{' '}
                  <span style={{ fontWeight: 600 }}>
                    time of the last modification
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Box 6: Description of Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1099a-description-img.webp" />
                  <source srcSet="/assets/images/f1099a-description-img.png" />
                  <img
                    src="/assets/images/f1099a-description-img.png"
                    loading="lazy"
                    alt="Form 1099-A-description of property"
                    width={431}
                    height={104}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter a
                  <span style={{ fontWeight: 600 }}>general description </span>{' '}
                  of the property:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Real property:</span>{' '}
                    Provide the <span style={{ fontWeight: 600 }}>address</span>
                    . If the address isn’t enough to identify it, include the
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      section, lot, and block.
                    </span>
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Personal property:</span>{' '}
                    Describe the{' '}
                    <span style={{ fontWeight: 600 }}>
                      type, make, and model
                    </span>{' '}
                    (e.g., “Car—2024 Honda Accord”).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Multiple items:</span>{' '}
                    Use a general category (e.g., “Office Equipment” for several
                    desks and computers).{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Crops:</span> Enter{' '}
                    <span style={{ fontWeight: 600 }}>“CCC”</span>
                    for crops forfeited on Commodity Credit Corporation
                    loans{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="import"
                >
                  Important deadlines for Form 1099-A?
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Filing method
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Borrower Copies
                          </TableCell>

                          <TableCell sx={{ fontSize: 15, fontWeight: 600 }}>
                            February 2, 2026{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Paper filing with the IRS
                          </TableCell>

                          <TableCell sx={{ fontSize: 15, fontWeight: 600 }}>
                            March 2, 2026
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            E-filing with the IRS
                          </TableCell>

                          <TableCell sx={{ fontSize: 15, fontWeight: 600 }}>
                            March 31, 2026
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    mb: 4,
                    mt: 2,
                    textAlign: 'left',
                    borderLeft: '3px solid rgb(102, 60, 0)',
                  }}
                >
                  <b>Note:</b> If the deadline falls on the weekend or federal
                  holidays, it is extended to the next businesses day.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="pen"
                >
                  Penalties for 1099-A form
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: '#ccc', fontWeight: 600 }}
                        >
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Violations
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            Penalty per Form
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Filed within 30 days late
                          </TableCell>

                          <TableCell sx={{ fontSize: 15, fontWeight: 600 }}>
                            $60
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Filed more than 30 days late but before August 1
                          </TableCell>

                          <TableCell sx={{ fontSize: 15, fontWeight: 600 }}>
                            $130
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Filed after August 1 or not filed at all
                          </TableCell>

                          <TableCell sx={{ fontSize: 15, fontWeight: 600 }}>
                            $340
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell sx={{ fontSize: 15 }}>
                            Intentional Disregard
                          </TableCell>

                          <TableCell sx={{ fontSize: 15, fontWeight: 600 }}>
                            $680
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  ðŸ‘‰ For more details, you can also refer to the{' '}
                  <Link
                    href="https://www.irs.gov/payments/information-return-penalties"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    information return penalties
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  How to E-File 1099-A Form with TaxZerone?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Before you start{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-a/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1099-A
                  </Link>
                  , make sure you have all the necessary information ready to
                  ensure a smooth filing process.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Information required to be reported on 1099-A form:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Lender information (name, address, TIN) </li>
                  <li>Borrower details (name, address, TIN) </li>
                  <li>Account number (if applicable) </li>
                  <li>Date of acquisition or abandonment </li>
                  <li>Balance of principal outstanding </li>
                  <li>Fair market value of the property </li>
                  <li>Borrower’s personal liability </li>
                  <li>Description of the property </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="step"
                >
                  Steps to E-file Form 1099-A with TaxZerone
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Step 1: Complete Form 1099-A
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Choose Form 1099-A in TaxZerone and fill out all the required
                  fields using the information you’ve gathered.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Step 2: Review and transmit the return
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Double-check all the information for accuracy. After you have
                  verified all the information, e-file form 1099-A with IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Step 3: Provide the borrower copy
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  After the form has been successfully submitted to the IRS,
                  provide a copy of 1099-A form to the borrower via ZeroneVault
                  or postal mailing.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        guidelines. For more details, please refer to the IRS
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1099ac.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          instructions of Form 1099-A
                        </Link>{' '}
                        and the{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          General Instructions of Information Returns
                        </Link>
                        .{' '}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File Form 1099-A Online with TaxZerone!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Stay IRS-compliant by e-filing Form 1099-A online with TaxZerone.
              Our secure e-filing solution ensures
              <Hidden smDown>
                <br />
              </Hidden>{' '}
              accurate form completion, thorough review, and timely delivery of
              borrower copies.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=f1099ainstructions`}
              >
                E-file Now
              </Button>
              {/* <Typography variant="body1" sx={{ mt: 1 }}>
                Takes 3 simple steps
              </Typography> */}
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099AInstructions;
